import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { getPlanInfo, PLAN_FEATURES } from './utils/plan';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Landing from './pages/Landing';
import Login from './pages/Login';
import LiveView from './pages/LiveView';
import Screenshots from './pages/Screenshots';
import Apps from './pages/Apps';
import Websites from './pages/Websites';
import Alerts from './pages/Alerts';
import Settings from './pages/Settings';
import Reports from './pages/Reports';
import Upgrade from './pages/Upgrade';
import Connection from './pages/Connection';
import Location from './pages/Location';
import CallLog from './pages/CallLog';
import Signup from './pages/Signup';
import './App.css';

/* -- Nav items ------------------------------------------------------------ */
const NAV_ITEMS = [
  { to: '/dashboard',              icon: '\uD83D\uDDA5\uFE0F', label: 'Live View' },
  { to: '/dashboard/screenshots',  icon: '\uD83D\uDCF8', label: 'Screenshots' },
  { to: '/dashboard/apps',         icon: '\uD83D\uDCF1', label: 'App Usage' },
  { to: '/dashboard/websites',     icon: '\uD83C\uDF10', label: 'Websites' },
  { to: '/dashboard/alerts',       icon: '\uD83D\uDD14', label: 'Alerts' },
  { to: '/dashboard/location',     icon: '\uD83D\uDCCD', label: 'Location' },
  { to: '/dashboard/calls',        icon: '\uD83D\uDCDE', label: 'Calls & SMS' },
  { to: '/dashboard/reports',      icon: '\uD83D\uDCCA', label: 'Reports' },
  { to: '/dashboard/connection',   icon: '\uD83D\uDD17', label: 'Connection' },
  { to: '/dashboard/settings',     icon: '\u2699\uFE0F', label: 'Settings' },
  { to: '/dashboard/upgrade',      icon: '\u2B50',       label: 'Upgrade' },
];

/* Mobile bottom bar: only 4 primary items + More */
const MOBILE_PRIMARY = [
  { to: '/dashboard',              icon: '\uD83C\uDFE0', label: 'Home' },
  { to: '/dashboard/screenshots',  icon: '\uD83D\uDCF8', label: 'Screens' },
  { to: '/dashboard/alerts',       icon: '\uD83D\uDD14', label: 'Alerts' },
  { to: '/dashboard/settings',     icon: '\u2699\uFE0F', label: 'Settings' },
];
const MOBILE_MORE = [
  { to: '/dashboard/apps',         icon: '\uD83D\uDCF1', label: 'App Usage' },
  { to: '/dashboard/websites',     icon: '\uD83C\uDF10', label: 'Websites' },
  { to: '/dashboard/reports',      icon: '\uD83D\uDCCA', label: 'Reports' },
  { to: '/dashboard/connection',   icon: '\uD83D\uDD17', label: 'Connection' },
  { to: '/dashboard/upgrade',      icon: '\u2B50',       label: 'Upgrade' },
];

/* -- Child Modal ---------------------------------------------------------- */
function ChildModal({ mode, kid, onClose, onSave, onDelete }) {
  const [name, setName] = useState(kid?.kid_name || '');
  const [username, setUsername] = useState(kid?.kid_username || '');
  const [age, setAge] = useState(kid?.age || '');
  const [gender, setGender] = useState(kid?.gender || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const usernameTimer = useRef(null);

  // Live username availability check
  useEffect(() => {
    const uname = username.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (!uname || uname.length < 3) { setUsernameStatus(null); return; }
    if (mode === 'edit' && uname === kid?.kid_username) { setUsernameStatus(null); return; }
    setUsernameStatus('checking');
    if (usernameTimer.current) clearTimeout(usernameTimer.current);
    usernameTimer.current = setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc('kid_username_exists', { p_username: uname });
        if (error) { setUsernameStatus(null); return; }
        setUsernameStatus(data === true ? 'taken' : 'available');
      } catch { setUsernameStatus(null); }
    }, 500);
    return () => { if (usernameTimer.current) clearTimeout(usernameTimer.current); };
  }, [username, mode, kid?.kid_username]);

  const handleSave = async () => {
    setError('');
    if (!name.trim()) return setError('Name is required');
    const uname = username.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (!uname || uname.length < 3) return setError('Username must be at least 3 characters');

    setSaving(true);
    try {
      if (mode === 'add' || uname !== kid?.kid_username) {
        const { data: taken } = await supabase.rpc('kid_username_exists', { p_username: uname });
        if (taken === true) {
          setError(`@${uname} is already taken`); setSaving(false); return;
        }
      }
      await onSave({
        kid_name: name.trim(),
        kid_username: uname,
        age: age ? Number(age) : null,
        gender: gender || null,
      }, kid?.id);
      onClose();
    } catch (e) {
      setError(e.message || 'Something went wrong');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (deleteConfirm !== 'DELETE') return;
    setDeleting(true);
    try {
      await onDelete(kid.id);
      onClose();
    } catch (e) {
      setError(e.message || 'Delete failed');
    }
    setDeleting(false);
  };

  const fieldStyle = {
    width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0',
    borderRadius: 8, fontSize: 14, outline: 'none',
  };
  const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 5000, padding: 20,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: '32px 28px',
        width: '92%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>
            {mode === 'add' ? 'Add Child' : 'Edit Child'}
          </h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 22, color: '#94a3b8',
            cursor: 'pointer', padding: '0 4px',
          }}>{'\u00D7'}</button>
        </div>

        {error && (
          <div style={{
            background: '#fee2e2', color: '#dc2626', padding: '8px 12px',
            borderRadius: 8, fontSize: 13, marginBottom: 16,
          }}>{error}</div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Child's Name *</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. Aarav" style={fieldStyle} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Username *</label>
          <input type="text" value={username}
            onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
            placeholder="e.g. aarav2015" style={{ ...fieldStyle, fontFamily: 'monospace' }} />
          {usernameStatus === 'checking' && (
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Checking...</div>
          )}
          {usernameStatus === 'available' && (
            <div style={{ fontSize: 12, color: '#10b981', marginTop: 4, fontWeight: 600 }}>
              {'\u2705'} @{username.trim().toLowerCase()} is available
            </div>
          )}
          {usernameStatus === 'taken' && (
            <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4, fontWeight: 600 }}>
              {'\u274C'} @{username.trim().toLowerCase()} is already taken
            </div>
          )}
          {!usernameStatus && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              Unique identifier (letters, numbers, underscore)
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Age</label>
            <input type="number" min="1" max="18" value={age}
              onChange={e => setAge(e.target.value)} placeholder="e.g. 10" style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Gender</label>
            <select value={gender} onChange={e => setGender(e.target.value)} style={fieldStyle}>
              <option value="">Select...</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '11px', background: '#f1f5f9', color: '#64748b',
            border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 1, padding: '11px',
            background: saving ? '#94a3b8' : '#0ea5e9', color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
            cursor: saving ? 'wait' : 'pointer',
          }}>{saving ? 'Saving...' : (mode === 'add' ? 'Add Child' : 'Save Changes')}</button>
        </div>

        {/* DELETE CHILD */}
        {mode === 'edit' && kid?.id && (
          <div style={{ marginTop: 24, borderTop: '1px solid #fee2e2', paddingTop: 20 }}>
            {!showDelete ? (
              <button onClick={() => setShowDelete(true)} style={{
                width: '100%', padding: '10px', background: '#fef2f2', color: '#dc2626',
                border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
              }}>🗑️ Delete This Child</button>
            ) : (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 10, padding: 16,
              }}>
                <p style={{ fontSize: 13, color: '#991b1b', marginBottom: 12, fontWeight: 600 }}>
                  This will permanently delete <strong>{kid.kid_name}</strong> and all their data
                  (devices, screenshots, websites, apps, alerts, settings, screen time).
                </p>
                <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>
                  Type <strong>DELETE</strong> to confirm:
                </p>
                <input type="text" value={deleteConfirm}
                  onChange={e => setDeleteConfirm(e.target.value)}
                  placeholder="Type DELETE" style={{
                    ...fieldStyle, borderColor: '#fecaca', marginBottom: 12,
                    fontFamily: 'monospace', textAlign: 'center', fontWeight: 700,
                  }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setShowDelete(false); setDeleteConfirm(''); }}
                    style={{
                      flex: 1, padding: '9px', background: '#f1f5f9', color: '#64748b',
                      border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}>Cancel</button>
                  <button onClick={handleDelete}
                    disabled={deleteConfirm !== 'DELETE' || deleting}
                    style={{
                      flex: 1, padding: '9px',
                      background: deleteConfirm === 'DELETE' ? '#dc2626' : '#e5e7eb',
                      color: deleteConfirm === 'DELETE' ? '#fff' : '#9ca3af',
                      border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      cursor: deleteConfirm === 'DELETE' ? 'pointer' : 'not-allowed',
                    }}>{deleting ? 'Deleting...' : 'Permanently Delete'}</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* -- Parent Profile Modal ------------------------------------------------- */
function ParentProfileModal({ customer, onClose, onSave }) {
  const [name, setName] = useState(customer?.name || '');
  const [email, setEmail] = useState(customer?.email || '');
  const [phone, setPhone] = useState(customer?.phone || '');
  const [whatsapp, setWhatsapp] = useState(customer?.whatsapp || '');
  const [alertEmailOn, setAlertEmailOn] = useState(customer?.alert_email !== false);
  const [alertPhoneOn, setAlertPhoneOn] = useState(customer?.alert_phone === true);
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fieldStyle = {
    width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0',
    borderRadius: 8, fontSize: 14, outline: 'none',
  };
  const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };

  const handleSave = async () => {
    setError(''); setSuccess('');
    if (!name.trim()) return setError('Name is required');
    if (!email.trim()) return setError('Email is required');

    if (showPassword && newPassword) {
      if (newPassword.length < 6) return setError('Password must be at least 6 characters');
      if (newPassword !== confirmPassword) return setError('Passwords do not match');
    }

    setSaving(true);
    try {
      await onSave({
        name: name.trim(), email: email.trim(),
        phone: phone.trim() || null, whatsapp: whatsapp.trim() || null,
        alert_email: alertEmailOn, alert_phone: alertPhoneOn,
      });

      if (showPassword && newPassword) {
        const { error: pwErr } = await supabase.auth.updateUser({ password: newPassword });
        if (pwErr) throw new Error('Profile saved but password change failed: ' + pwErr.message);
      }

      setSuccess('Profile updated!');
      setTimeout(() => onClose(), 800);
    } catch (e) {
      setError(e.message || 'Something went wrong');
    }
    setSaving(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 5000, padding: 20,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: '32px 28px',
        width: '92%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>Edit Profile</h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 22, color: '#94a3b8',
            cursor: 'pointer', padding: '0 4px',
          }}>{'\u00D7'}</button>
        </div>

        {error && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
        {success && <div style={{ background: '#dcfce7', color: '#16a34a', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{success}</div>}

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Your Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Vinayak" style={fieldStyle} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={fieldStyle} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Phone</label>
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 98765 43210" style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>WhatsApp</label>
            <input type="tel" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} placeholder="+91 98765 43210" style={fieldStyle} />
          </div>
        </div>

        {/* Alert Preferences */}
        <div style={{ marginBottom: 20, background: '#f8fafc', borderRadius: 10, padding: 16, border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Alert Preferences</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <div onClick={() => setAlertEmailOn(!alertEmailOn)} style={{
                width: 44, height: 24, borderRadius: 12,
                background: alertEmailOn ? '#0ea5e9' : '#cbd5e1',
                position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s',
              }}>
                <div style={{
                  position: 'absolute', top: 3, left: alertEmailOn ? 23 : 3,
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
              <span style={{ fontSize: 13, color: '#374151' }}>Email alerts</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <div onClick={() => setAlertPhoneOn(!alertPhoneOn)} style={{
                width: 44, height: 24, borderRadius: 12,
                background: alertPhoneOn ? '#0ea5e9' : '#cbd5e1',
                position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s',
              }}>
                <div style={{
                  position: 'absolute', top: 3, left: alertPhoneOn ? 23 : 3,
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
              <span style={{ fontSize: 13, color: '#374151' }}>Phone/WhatsApp alerts</span>
            </label>
          </div>
        </div>

        {/* Change Password */}
        {!showPassword ? (
          <button onClick={() => setShowPassword(true)} style={{
            width: '100%', padding: '10px', background: '#f8fafc', color: '#475569',
            border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', marginBottom: 16,
          }}>🔑 Change Password</button>
        ) : (
          <div style={{ marginBottom: 16, background: '#f8fafc', borderRadius: 10, padding: 16, border: '1px solid #e2e8f0' }}>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>New Password</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                placeholder="Min 6 characters" style={fieldStyle} />
            </div>
            <div>
              <label style={labelStyle}>Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password" style={fieldStyle} />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '11px', background: '#f1f5f9', color: '#64748b',
            border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 1, padding: '11px',
            background: saving ? '#94a3b8' : '#0ea5e9', color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
            cursor: saving ? 'wait' : 'pointer',
          }}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}
/* -- No Device Banner (wraps page content) -------------------------------- */
function NoDeviceBanner({ kidName, kidId }) {
  const navigate = useNavigate();
  return (
    <div style={{
      textAlign: 'center', padding: '80px 24px',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}>
      <div style={{
        width: 80, height: 80, borderRadius: 20,
        background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 36, marginBottom: 20,
      }}>🔗</div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', margin: '0 0 8px' }}>
        No Device Connected{kidName ? ` for ${kidName}` : ''}
      </h2>
      <p style={{ color: '#94a3b8', fontSize: 14, maxWidth: 420, lineHeight: 1.6, marginBottom: 28 }}>
        Install the ParentsProtection agent on {kidName ? `${kidName}'s` : "your child's"} computer
        to start monitoring screen time, websites, apps, and more.
      </p>
      <button
        onClick={() => navigate('/dashboard/connection')}
        style={{
          background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
          color: '#fff', border: 'none', borderRadius: 10,
          padding: '12px 32px', fontSize: 15, fontWeight: 700,
          cursor: 'pointer', boxShadow: '0 4px 14px rgba(14,165,233,0.3)',
        }}
      >
        Connect a Device
      </button>
    </div>
  );
}

/* -- Mobile Bottom Nav ---------------------------------------------------- */
function MobileNav({ unread, moreOpen, setMoreOpen, moreRef }) {
  const navigate = useNavigate();
  const moreMenuRef = useRef(null);

  useEffect(() => {
    if (!moreOpen) return;
    const handler = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target) &&
          moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('touchstart', handler);
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('mousedown', handler);
    };
  }, [moreOpen, setMoreOpen, moreRef]);

  return (
    <nav className="mobile-nav">
      {MOBILE_PRIMARY.map(({ to, icon, label }) => (
        <NavLink
          key={to}
          to={to}
          end={to === '/dashboard'}
          className={({ isActive }) => `mobile-nav-item${isActive ? ' active' : ''}`}
          onClick={() => setMoreOpen(false)}
        >
          <span className="mobile-nav-icon">{icon}</span>
          <span className="mobile-nav-label">{label}</span>
          {label === 'Alerts' && unread > 0 && (
            <span className="mobile-nav-badge">{unread}</span>
          )}
        </NavLink>
      ))}
      <button
        ref={moreRef}
        className={`mobile-nav-item mobile-nav-more${moreOpen ? ' active' : ''}`}
        onClick={() => setMoreOpen(v => !v)}
      >
        <span className="mobile-nav-icon">{'\u2630'}</span>
        <span className="mobile-nav-label">More</span>
      </button>

      {moreOpen && (
        <div className="mobile-more-menu" ref={moreMenuRef}>
          {MOBILE_MORE.map(({ to, icon, label }) => (
            <button
              key={to}
              className="mobile-more-item"
              onClick={() => { navigate(to); setMoreOpen(false); }}
            >
              <span className="mobile-more-icon">{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}

/* -- Dashboard ------------------------------------------------------------ */
function Dashboard() {
  const { user, customer, kids, signOut, refreshProfile } = useAuth();

  const [selectedKid,  setSelectedKid]  = useState(null);
  const [plan,         setPlan]         = useState('free');
  const [planFeatures, setPlanFeatures] = useState(PLAN_FEATURES.free);
  const [planInfo,     setPlanInfo]     = useState(null);
  const [unread,       setUnread]       = useState(0);

  // Child modal
  const [childModal, setChildModal] = useState(null);
  // Parent profile modal
  const [showProfileModal, setShowProfileModal] = useState(false);
  // Device check for selected kid
  const [hasDevice, setHasDevice] = useState(null);
  const [deviceLoading, setDeviceLoading] = useState(false);

  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  // Device filter: 'all' | 'mac' | 'android'
  const [deviceFilter, setDeviceFilter] = useState('all');

  useEffect(() => {
    if (kids.length === 0) { setSelectedKid(null); return; }
    if (!selectedKid || !kids.find(k => k.id === selectedKid.id)) {
      setSelectedKid(kids[0]);
    }
  }, [kids, selectedKid]);

  // Check if selected kid has a device
  useEffect(() => {
    if (!selectedKid?.id) { setHasDevice(null); return; }
    setDeviceLoading(true);
    supabase
      .from('devices')
      .select('id')
      .eq('kid_id', selectedKid.id)
      .limit(1)
      .then(({ data }) => {
        setHasDevice(data && data.length > 0);
        setDeviceLoading(false);
      })
      .catch(() => {
        setHasDevice(null);
        setDeviceLoading(false);
      });
  }, [selectedKid?.id]);

  // Read plan from customer
  useEffect(() => {
    if (!customer) return;
    (async () => {
      const info = getPlanInfo(customer);
      if (info.isExpired && info.rawPlan !== 'free') {
        await supabase.from('customers').update({ plan: 'free' }).eq('id', customer.id);
        info.plan = 'free';
        info.features = PLAN_FEATURES.free;
        info.isFree = true;
        info.isPaid = false;
        info.isTrial = false;
      }
      setPlan(info.plan);
      setPlanFeatures(info.features);
      setPlanInfo(info);
    })();
  }, [customer]); // eslint-disable-line

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      const { count } = await supabase
        .from('alerts').select('*', { count: 'exact', head: true })
        .eq('is_read', false).eq('parent_id', user.id);
      setUnread(count || 0);
    };
    fetchUnread();
    const ch = supabase.channel('alerts-badge')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'alerts', filter: `parent_id=eq.${user.id}` }, fetchUnread)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [user]);

  const handleSaveChild = useCallback(async (data, kidId) => {
    if (kidId) {
      const { error } = await supabase.from('kids').update(data).eq('id', kidId);
      if (error) throw error;
    } else {
      let custId = customer?.id;

      if (!custId && user) {
        const { data: existing } = await supabase
          .from('customers').select('id').eq('auth_id', user.id).limit(1);
        if (existing?.[0]) {
          custId = existing[0].id;
        } else {
          const { data: newCust, error: custErr } = await supabase.from('customers').insert({
            auth_id: user.id,
            name: user.email?.split('@')[0] || 'Parent',
            email: user.email || '',
          }).select('id').single();
          if (custErr) throw new Error('Could not create account: ' + custErr.message);
          custId = newCust.id;
        }
      }

      if (!custId) throw new Error('Could not find your account. Please log out and log in again.');

      const { error } = await supabase.from('kids').insert({
        customer_id: custId,
        ...data,
      });
      if (error) throw error;
    }
    await refreshProfile();
  }, [customer, user, refreshProfile]);

  const handleDeleteChild = useCallback(async (kidId) => {
    try {
      // Cascade delete from all related tables
      const tables = ['devices', 'screenshots', 'websites', 'app_usage', 'alerts', 'settings', 'screen_time'];
      for (const table of tables) {
        await supabase.from(table).delete().eq('kid_id', kidId);
      }
      // Delete the kid row itself
      const { error } = await supabase.from('kids').delete().eq('id', kidId);
      if (error) throw error;
      await refreshProfile();
    } catch (e) {
      alert('Delete failed: ' + e.message);
      throw e;
    }
  }, [refreshProfile]);

  // Save parent profile
  const handleSaveProfile = useCallback(async (data) => {
    if (!customer?.id) throw new Error('Account not found');
    const { error } = await supabase.from('customers').update(data).eq('id', customer.id);
    if (error) throw error;
    // If email changed, update auth email too
    if (data.email && data.email !== customer.email) {
      const { error: authErr } = await supabase.auth.updateUser({ email: data.email });
      if (authErr) console.warn('Auth email update failed:', authErr.message);
    }
    await refreshProfile();
  }, [customer, refreshProfile]);

  const childName = selectedKid?.kid_name || 'No child';
  const daysLeft = planInfo?.daysLeft || 0;

  const getPlanBadge = () => {
    if (plan === 'trial') return { label: `Trial \u00B7 ${daysLeft}d`, bg: '#d97706' };
    if (plan === 'plus') return { label: `Plus \u00B7 ${daysLeft}d`, bg: '#2563eb' };
    if (plan === 'family') return { label: `Family \u00B7 ${daysLeft}d`, bg: '#7c3aed' };
    return { label: 'Free Forever', bg: '#6b7280' };
  };
  const planBadge = getPlanBadge();

  const kidProps = { kidId: selectedKid?.id || null, parentId: user?.id || null, deviceFilter };
  const planProps = { plan, features: planFeatures };



  // Wrapper: shows NoDeviceBanner for monitoring pages when no device
  const withDeviceCheck = (element) => {
    if (deviceLoading) {
      return (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 60, color: '#64748b', fontSize: 14,
        }}>
          Loading...
        </div>
      );
    }
    if (hasDevice === false) {
      return <NoDeviceBanner kidName={selectedKid?.kid_name} kidId={selectedKid?.id} />;
    }
    return element;
  };

  return (
    <div className="app-shell">
      {/* Expiry banner */}
      {plan === 'free' && planInfo?.isExpired && planInfo?.rawPlan !== 'free' && (
        <div style={{
          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
          color: '#fff', padding: '10px 16px',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          gap: 10, fontSize: 13, fontWeight: 600, flexShrink: 0, flexWrap: 'wrap',
          textAlign: 'center',
        }}>
          {planInfo.rawPlan === 'trial'
            ? '\u26A0\uFE0F Your free trial has expired! You are now on the Free Forever plan.'
            : `\u26A0\uFE0F Your ${planInfo.rawPlan.charAt(0).toUpperCase() + planInfo.rawPlan.slice(1)} subscription has expired! You are now on the Free Forever plan.`}
          <NavLink to="/dashboard/upgrade"
            style={{ color: '#fff', textDecoration: 'underline', fontWeight: 700 }}>
            Renew now
          </NavLink>
        </div>
      )}
      {plan === 'trial' && daysLeft <= 7 && (
        <div style={{
          background: daysLeft <= 1 ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'linear-gradient(135deg, #f59e0b, #d97706)',
          color: '#fff', padding: '10px 16px',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          gap: 10, fontSize: 13, fontWeight: 600, flexShrink: 0, flexWrap: 'wrap',
          textAlign: 'center',
        }}>
          {daysLeft === 0 ? 'Your free trial expires today!' : `Your free trial ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}!`}
          <NavLink to="/dashboard/upgrade"
            style={{ color: '#fff', textDecoration: 'underline', fontWeight: 700 }}>
            Upgrade now
          </NavLink>
        </div>
      )}
      {(plan === 'plus' || plan === 'family') && daysLeft <= 7 && (
        <div style={{
          background: daysLeft <= 1 ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'linear-gradient(135deg, #f59e0b, #d97706)',
          color: '#fff', padding: '10px 16px',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          gap: 10, fontSize: 13, fontWeight: 600, flexShrink: 0, flexWrap: 'wrap',
          textAlign: 'center',
        }}>
          {daysLeft === 0
            ? `Your ${plan.charAt(0).toUpperCase() + plan.slice(1)} subscription expires today!`
            : `Your ${plan.charAt(0).toUpperCase() + plan.slice(1)} subscription renews in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`}
          <NavLink to="/dashboard/upgrade"
            style={{ color: '#fff', textDecoration: 'underline', fontWeight: 700 }}>
            Renew now
          </NavLink>
        </div>
      )}

      <div className="dashboard-body">
        {/* Sidebar */}
        <aside className="sidebar">
          {/* Brand */}
          <div className="sidebar-header">
            <div className="child-avatar">{'\uD83D\uDEE1\uFE0F'}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="brand">ParentsProtection</div>
              <div className="sub">{customer?.name || 'Dashboard'}</div>
            </div>
            <span style={{
              background: planBadge.bg, color: '#fff',
              padding: '2px 8px', borderRadius: 99,
              fontSize: 9, fontWeight: 800, letterSpacing: '0.04em',
              flexShrink: 0,
            }}>
              {planBadge.label}
            </span>
          </div>

          {/* Children */}
          <div className="sidebar-kids">
            <div className="sidebar-kids-header">
              <span className="sidebar-kids-title">Children</span>
              <button
                className="sidebar-kids-add"
                onClick={() => setChildModal({ mode: 'add' })}
                title="Add child"
              >+</button>
            </div>

            {kids.length === 0 ? (
              <div className="sidebar-kids-empty">
                No children yet
                <button
                  onClick={() => setChildModal({ mode: 'add' })}
                  className="sidebar-kids-add-btn"
                >
                  + Add your first child
                </button>
              </div>
            ) : (
              <div className="sidebar-kids-list">
                {kids.map(kid => {
                  const isSelected = selectedKid?.id === kid.id;
                  return (
                    <div
                      key={kid.id}
                      className={`sidebar-kid${isSelected ? ' active' : ''}`}
                      onClick={() => setSelectedKid(kid)}
                    >
                      <div className="sidebar-kid-avatar">
                        {kid.kid_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="sidebar-kid-info">
                        <div className="sidebar-kid-name">{kid.kid_name}</div>
                        <div className="sidebar-kid-username">@{kid.kid_username}</div>
                      </div>
                      <button
                        className="sidebar-kid-edit"
                        onClick={(e) => { e.stopPropagation(); setChildModal({ mode: 'edit', kid }); }}
                        title="Edit"
                      >{'\u270E'}</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Device filter toggle */}
          <div style={{
            display: 'flex', gap: 4, padding: '8px 16px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}>
            {[
              { key: 'all', icon: '\uD83D\uDCBB', label: 'All' },
              { key: 'mac', icon: '\uD83D\uDDA5\uFE0F', label: 'PC' },
              { key: 'android', icon: '\uD83D\uDCF1', label: 'Phone' },
            ].map(({ key, icon, label }) => (
              <button
                key={key}
                onClick={() => setDeviceFilter(key)}
                style={{
                  flex: 1, padding: '6px 0', fontSize: 11, fontWeight: 700,
                  borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: deviceFilter === key ? 'rgba(59,130,246,0.2)' : 'transparent',
                  color: deviceFilter === key ? '#3b82f6' : '#64748b',
                  transition: 'all 0.15s',
                }}
              >
                {icon} {label}
              </button>
            ))}
          </div>

          {/* Desktop Nav */}
          <nav className="desktop-nav">
            {NAV_ITEMS.map(({ to, icon, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/dashboard'}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                style={to === '/dashboard/upgrade' ? { color: '#f59e0b' } : {}}
              >
                {icon} {label}
                {label === 'Alerts' && unread > 0 && (
                  <span className="nav-badge">{unread}</span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Footer — now with edit profile button */}
          <div className="sidebar-footer" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
            <div
              onClick={() => setShowProfileModal(true)}
              style={{
                fontSize: 13, fontWeight: 600, color: '#e2e8f0',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                cursor: 'pointer', padding: '4px 0',
              }}
            >
              <span>{customer?.name || 'Parent'}</span>
              <span style={{ fontSize: 14, color: '#94a3b8' }} title="Edit Profile">{'\u270E'}</span>
            </div>
            <button
              className="btn-sm"
              onClick={async () => { await signOut(); window.location.href = '/'; }}
              style={{
                background: 'rgba(239,68,68,0.1)', color: '#f87171',
                border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8,
                padding: '7px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Log Out
            </button>
          </div>
        </aside>

        {/* Main content — monitoring pages wrapped with device check */}
        <main className="content">
          <Routes>
            <Route index element={withDeviceCheck(<LiveView childName={childName} {...planProps} {...kidProps} />)} />
            <Route path="screenshots" element={withDeviceCheck(<Screenshots {...planProps} {...kidProps} />)} />
            <Route path="apps" element={withDeviceCheck(<Apps {...kidProps} />)} />
            <Route path="websites" element={withDeviceCheck(<Websites {...planProps} {...kidProps} />)} />
            <Route path="alerts" element={withDeviceCheck(<Alerts {...kidProps} />)} />
            <Route path="reports" element={withDeviceCheck(<Reports {...planProps} {...kidProps} />)} />
            <Route path="location" element={withDeviceCheck(<Location {...kidProps} />)} />
            <Route path="calls" element={withDeviceCheck(<CallLog {...kidProps} />)} />
            {/* Connection, Settings, Upgrade always accessible */}
            <Route path="connection" element={<Connection {...kidProps} />} />
            <Route path="settings" element={
              <Settings
                {...kidProps}
                onNameChange={() => {}}
                onPlanChange={(p) => { setPlan(p); setPlanFeatures(PLAN_FEATURES[p] || PLAN_FEATURES.free); }}
              />
            } />
            <Route path="upgrade" element={<Upgrade currentPlan={plan} />} />
          </Routes>
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <MobileNav unread={unread} moreOpen={moreOpen} setMoreOpen={setMoreOpen} moreRef={moreRef} />

      {/* Child Modal */}
      {childModal && (
        <ChildModal
          mode={childModal.mode}
          kid={childModal.kid}
          onClose={() => setChildModal(null)}
          onSave={handleSaveChild}
          onDelete={handleDeleteChild}
        />
      )}

      {/* Parent Profile Modal */}
      {showProfileModal && (
        <ParentProfileModal
          customer={customer}
          onClose={() => setShowProfileModal(false)}
          onSave={handleSaveProfile}
        />
      )}
    </div>
  );
}

/* -- Route guards --------------------------------------------------------- */
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0f172a',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#64748b', fontSize: 14, flexDirection: 'column', gap: 12,
      }}>
        <div style={{ fontSize: 40 }}>&#x1F6E1;&#xFE0F;</div>
        <div>Loading dashboard...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Login />;
  if (user) return <Navigate to="/dashboard" />;
  return <Login />;
}

function SignupRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Signup />;
  if (user) return <Navigate to="/dashboard" />;
  return <Signup />;
}

/* -- App root ------------------------------------------------------------- */
function AppInner() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/signup" element={<SignupRoute />} />
        <Route path="/dashboard/*" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      </Routes>
    </Router>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}